import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { normalizeError } from "../../../operationFeedback/normalizeError";
import {
  createEmployeeSigningRequest,
  createServiceAgreementSigningRequest,
  createSigningRequest,
  downloadSignedDocument,
  fetchSigningRequests,
  pollSigningRequest,
} from "../../../api/signing";
import {
  mapSignersForPosten,
  signerReadyForPosten,
} from "../text-documents/signatureSignerUtils";
import { PMButton } from "../../utils/PMButton";

const statusClass = {
  completed: "bg-green-100 text-green-800",
  signed: "bg-green-100 text-green-800",
  failed: "bg-red-100 text-red-800",
  rejected: "bg-red-100 text-red-800",
  expired: "bg-red-100 text-red-800",
  sent: "bg-blue-100 text-blue-800",
  in_progress: "bg-blue-100 text-blue-800",
  queued: "bg-yellow-100 text-yellow-800",
};

function signingClientError(t, request) {
  if (!request?.error_code && !request?.error_message) return null;
  const known = new Set([
    "create_failed",
    "dispatch_unconfirmed",
    "activation_failed",
    "poll_failed",
  ]);
  const code = known.has(request.error_code) ? request.error_code : "poll_failed";
  return t(`signing.errors.${code}`);
}

export function SigningPanel({
  signableType,
  signableId,
  title,
  nonSensitiveTitle,
  description,
  defaultSigner = {},
  documentSigners = [],
  signingEnabled = true,
  serviceAgreementId,
  userId,
  employeeId,
  onChanged,
}) {
  const { t } = useTranslation();
  const [feedback, setFeedback] = useState(null);
  const sending = useRef(false);
  const polling = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const idsAtUnknownRef = useRef(new Set());
  const [form, setForm] = useState({
    name: defaultSigner.name || "",
    national_identity_number: defaultSigner.national_identity_number || "",
    email: defaultSigner.email || "",
    mobile: defaultSigner.mobile || "",
  });

  useEffect(() => {
    setForm({
      name: defaultSigner.name || "",
      national_identity_number: defaultSigner.national_identity_number || "",
      email: defaultSigner.email || "",
      mobile: defaultSigner.mobile || "",
    });
  }, [defaultSigner.name, defaultSigner.national_identity_number, defaultSigner.email, defaultSigner.mobile]);

  useEffect(() => {
    if (signableId) loadRequests();
  }, [signableType, signableId]);

  const latestRequest = useMemo(() => requests?.[0], [requests]);
  const latestInflight = ["queued", "sent", "in_progress"].includes(latestRequest?.status);
  const sendLocked = !signingEnabled || isSending || Boolean(feedback?.unknown) || latestInflight;
  const requestError = signingClientError(t, latestRequest);

  const loadRequests = async () => {
    setIsLoading(true);
    try {
      const result = await fetchSigningRequests({
        signable_type: signableType,
        signable_id: signableId,
      });
      const rows = result?.data;
      setRequests(Array.isArray(rows) ? rows : []);
      // A successful list answers whether the request was created, so the
      // unknown-outcome lock must not keep «Ny forespørsel» and «Send» disabled.
      if (alive.current && Array.isArray(rows)) {
        setFeedback((current) => {
          if (!current?.unknown) return current;
          const created = rows.some((row) => !idsAtUnknownRef.current.has(row.id));
          return created ? null : current;
        });
      }
    } catch (error) {
      if (alive.current) setFeedback({ error: true, message: t("signing.errors.load_failed") });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const useDocumentSigners =
    signableType === "TextDocument" && Array.isArray(documentSigners) && documentSigners.length > 0;

  const configuredSigners = useDocumentSigners ? mapSignersForPosten(documentSigners) : [];
  const configuredSignersValid =
    configuredSigners.length > 0 && configuredSigners.every((signer) => signerReadyForPosten(signer));

  const handleCreate = async (event) => {
    event.preventDefault();
    if (sending.current || sendLocked) return;
    sending.current = true;
    setIsSending(true); setFeedback(null);
    let confirmed;

    try {
      const signers = useDocumentSigners
        ? configuredSigners.map(({ name, email, mobile, national_identity_number, employee_id, user_id }) => ({
            name,
            email,
            mobile,
            national_identity_number,
            employee_id,
            user_id,
          }))
        : [
            {
              name: form.name,
              national_identity_number: form.national_identity_number,
              email: form.email,
              mobile: form.mobile,
            },
          ];

      const payload = {
        signable_type: signableType,
        signable_id: signableId,
        title,
        non_sensitive_title: nonSensitiveTitle || title,
        description,
        signers,
      };

      if (serviceAgreementId) {
        confirmed = await createServiceAgreementSigningRequest(serviceAgreementId, payload);
      } else if (userId && employeeId) {
        confirmed = await createEmployeeSigningRequest(userId, employeeId, payload);
      } else {
        confirmed = await createSigningRequest(payload);
      }

      if (!confirmed?.id) throw new Error("Unconfirmed signing request");
      if (!alive.current) return;
      setRequests((rows) => [confirmed, ...rows.filter((row) => row.id !== confirmed.id)]);
      setFeedback({ message: t("signing.created") });
      setFormOpen(false);
    } catch (error) {
      if (alive.current) {
        const normalized = normalizeError(error);
        const unknown = normalized.outcome === "unknown" || !normalized.httpStatus || normalized.httpStatus >= 500;
        if (unknown) {
          idsAtUnknownRef.current = new Set((requests || []).map((row) => row.id));
        }
        setFeedback({ error: true, unknown, message: unknown ? t("agreementWorkflow.unknown") : normalized.fieldErrors.map((row) => row.message).join(". ") || t("signing.errors.create_failed") });
      }
    } finally {
      sending.current = false;
      if (alive.current) setIsSending(false);
    }
    if (confirmed?.id && alive.current) {
      try { await onChanged?.(); }
      catch { if (alive.current) setFeedback({ error: true, message: t("agreementWorkflow.updatedRefreshFailed") }); }
    }
  };

  const handlePoll = async (requestId) => {
    if (polling.current) return;
    polling.current = true;
    let confirmed = false;
    try {
      const result = await pollSigningRequest(requestId);
      setRequests((prev) => prev.map((item) => (item.id === result.id ? result : item)));
      confirmed = true;
      if (alive.current) setFeedback({ message: t("signing.refreshed") });
    } catch (error) {
      if (alive.current) setFeedback({ error: true, message: t("signing.errors.poll_failed") });
    } finally { polling.current = false; }
    if (confirmed && alive.current) {
      try { await onChanged?.(); }
      catch { if (alive.current) setFeedback({ error: true, message: t("agreementWorkflow.updatedRefreshFailed") }); }
    }
  };

  const handleDownload = async (requestId) => {
    try {
      const response = await downloadSignedDocument(requestId);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `signed-document-${requestId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      if (alive.current) setFeedback({ error: true, message: t("signing.errors.download_failed") });
    }
  };

  return (
    <div className="rounded-md border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t("signing.title")}</h3>
          <p className="mt-1 text-sm text-gray-500">{t("signing.description")}</p>
        </div>
        <div className="w-44">
          <PMButton type="button" disabled={sendLocked} onClick={() => setFormOpen((open) => !open)}>
            {t("signing.new_request")}
          </PMButton>
        </div>
      </div>

      {feedback && <div role={feedback.error ? "alert" : "status"} className="mt-4 rounded-xl bg-slate-50 p-3 text-sm">{feedback.message}{feedback.unknown && <button type="button" className="ml-2 underline" onClick={loadRequests}>{t("agreementWorkflow.checkStatus")}</button>}</div>}
      {latestRequest && (
        <div className="mt-4 rounded-md bg-gray-50 p-3">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-medium text-gray-900">{latestRequest.title}</div>
              <div className="text-xs text-gray-500">
                {t("signing.sender_reference")}: {latestRequest.sender_reference}
              </div>
            </div>
            <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${statusClass[latestRequest.status] || "bg-gray-100 text-gray-800"}`}>
              {t(`signing.status.${latestRequest.status}`, latestRequest.status)}
            </span>
          </div>

          <div className="mt-3 space-y-2">
            {(latestRequest.signers || []).map((signer) => (
              <div key={signer.id} className="flex items-center justify-between text-sm">
                <span>{signer.name || signer.email || signer.mobile || signer.national_identity_number_masked}</span>
                <span className={`rounded-full px-2 py-0.5 text-xs ${statusClass[signer.status] || "bg-gray-100 text-gray-700"}`}>
                  {t(`signing.status.${signer.status}`, signer.status)}
                </span>
              </div>
            ))}
          </div>

          {requestError && (
            <div className="mt-3 rounded bg-red-50 p-2 text-sm text-red-700">{requestError}</div>
          )}

          <div className="mt-4 flex gap-2">
            <PMButton type="button" styleType="minimal" onClick={() => handlePoll(latestRequest.id)}>
              {t("signing.refresh_status")}
            </PMButton>
            <PMButton
              type="button"
              styleType="minimal"
              disabled={latestRequest.status !== "completed"}
              onClick={() => handleDownload(latestRequest.id)}
            >
              {t("signing.download_signed")}
            </PMButton>
          </div>
        </div>
      )}

      {!latestRequest && !isLoading && (
        <div className="mt-4 rounded-md bg-gray-50 p-3 text-sm text-gray-600">{t("signing.empty")}</div>
      )}

      {formOpen && useDocumentSigners ? (
        <div className="mt-4 space-y-3">
          {!signingEnabled ? (
            <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">
              {t("textDocuments.signature.signing_requires_signers")}
            </div>
          ) : (
            <>
              <div className="rounded-md bg-gray-50 p-3 text-sm text-gray-700">
                {t("textDocuments.signature.signing_uses_configured_signers")}
              </div>
              <ul className="space-y-2">
                {configuredSigners.map((signer, index) => (
                  <li
                    key={`${signer.name}-${index}`}
                    className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
                  >
                    <div className="font-medium text-gray-900">{signer.name}</div>
                    {signer.title ? (
                      <div className="text-xs text-gray-500">{signer.title}</div>
                    ) : null}
                    {signer.employee_id ? (
                      <div className="text-xs text-gray-500">
                        {t("textDocuments.signature.employee_linked")}
                      </div>
                    ) : (
                      <div className="text-xs text-gray-500">
                        {[maskNationalIdentityNumber(signer.national_identity_number), signer.email, signer.mobile]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
              {!configuredSignersValid ? (
                <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-800">
                  {t("textDocuments.signature.signing_missing_identity")}
                </div>
              ) : null}
              <div className="rounded bg-yellow-50 p-2 text-sm text-yellow-800">
                {t("signing.identity_warning")}
              </div>
              <div className="flex justify-end">
                <div className="w-44">
                  <PMButton
                    type="button"
                    loading={isSending}
                    disabled={sendLocked || !configuredSignersValid}
                    onClick={handleCreate}
                  >
                    {t("signing.send")}
                  </PMButton>
                </div>
              </div>
            </>
          )}
        </div>
      ) : null}

      {formOpen && !useDocumentSigners ? (
        <form onSubmit={handleCreate} className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label={t("signing.signer_name")} name="name" value={form.name} onChange={handleChange} />
          <Input
            label={t("signing.national_identity_number")}
            name="national_identity_number"
            value={form.national_identity_number}
            onChange={handleChange}
            autoComplete="off"
          />
          <Input label={t("signing.email")} name="email" value={form.email} onChange={handleChange} type="email" />
          <Input label={t("signing.mobile")} name="mobile" value={form.mobile} onChange={handleChange} />
          <div className="sm:col-span-2 rounded bg-yellow-50 p-2 text-sm text-yellow-800">
            {t("signing.identity_warning")}
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <div className="w-44">
              <PMButton type="submit" loading={isSending} disabled={sendLocked}>
                {t("signing.send")}
              </PMButton>
            </div>
          </div>
        </form>
      ) : null}
    </div>
  );
}

function maskNationalIdentityNumber(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (!digits) return "";
  if (digits.length <= 6) return "••••";
  return `${digits.slice(0, 6)}•••••`;
}

function Input({ label, ...props }) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-gray-700">{label}</span>
      <input
        {...props}
        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
      />
    </label>
  );
}
